/**
 * Upgrade Modal
 * Displays pricing and handles Paddle checkout for upgrading to Pro
 */

import React, { useState } from 'react';
import { SubscriptionContext } from '../contexts/SubscriptionContext';
import './UpgradeModal.css';

// Safe hook that doesn't throw when context is missing
const useSubscriptionSafe = () => {
  const context = React.useContext(SubscriptionContext);
  return context;
};

const UpgradeModal = ({ close, feature, subscriptionProps }) => {
  // Use props if provided (when opened outside context), otherwise use context
  const contextValue = useSubscriptionSafe();
  const { prices = [], openCheckout, subscription, paddleReady, openPortal } = subscriptionProps || contextValue || {};
  
  const [selectedInterval, setSelectedInterval] = useState('yearly');
  const [loading, setLoading] = useState(false);

  const monthlyPrice = prices?.find(p => p.interval === 'monthly');
  const yearlyPrice = prices?.find(p => p.interval === 'yearly');

  // Pricing comes from the same stub API the rest of the app uses
  // (Paddle will be the authority once wired up). Fall back to the current
  // figures only if the payload is missing, so the modal never shows NaN.
  const monthlyAmount = Number(monthlyPrice?.price) || 12.99;
  const yearlyAmount = Number(yearlyPrice?.price) || 119.88;
  const money = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : String(n));
  const effectiveMonthly = Math.round((yearlyAmount / 12) * 100) / 100;

  const handleUpgrade = async () => {
    const priceId = selectedInterval === 'yearly' ? yearlyPrice?.id : monthlyPrice?.id;
    if (!priceId) {
      alert('Pricing not available. Please try again later.');
      return;
    }

    try {
      setLoading(true);
      await openCheckout(priceId);
      // Modal stays open, checkout is overlay
    } catch (error) {
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const featureDescriptions = {
    ai_recommendations: 'AI-powered daily recommendations',
    ai_coach_settings: 'Personalized AI coach settings',
    ai_inference: 'AI pose detection & form feedback',
    ai_pose_detection: 'Real-time exercise form analysis',
    create_dojo: 'Create and manage your own dojo'
  };

  const featureLabel = featureDescriptions[feature] || 'This feature';

  // If already subscribed, show manage option
  if (subscription?.tier === 'pro') {
    return (
      <div className="upgrade-modal">
        <div className="upgrade-header">
          <h2>✨ You're on Pro!</h2>
        </div>
        <div className="upgrade-body">
          <p className="upgrade-current-plan">
            You have full access to all Pro features.
          </p>
          <button className="upgrade-manage-btn" onClick={openPortal}>
            Manage Subscription
          </button>
        </div>
        <div className="upgrade-footer">
          <button className="upgrade-close-btn" onClick={close}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="upgrade-modal">
      <div className="upgrade-header">
        <h2>🚀 Upgrade to Pro</h2>
        {feature && (
          <p className="upgrade-feature-prompt">
            <span className="upgrade-lock">🔒</span> {featureLabel} requires Pro
          </p>
        )}
      </div>

      <div className="upgrade-body">
        <div className="upgrade-features">
          <h3>Everything in Pro:</h3>
          <ul>
            <li>🤖 AI-powered daily recommendations</li>
            <li>🎯 Personalized AI coach with custom tone</li>
            <li>📸 Pose detection & form feedback on your videos</li>
            <li>📊 Advanced analytics & insights</li>
            <li>🥋 Create and manage your own dojo</li>
            <li>⚡ Priority support</li>
          </ul>
        </div>

        <div className="upgrade-pricing">
          <div className="upgrade-interval-toggle">
            <button
              className={`interval-btn ${selectedInterval === 'monthly' ? 'active' : ''}`}
              onClick={() => setSelectedInterval('monthly')}
            >
              Monthly
            </button>
            <button
              className={`interval-btn ${selectedInterval === 'yearly' ? 'active' : ''}`}
              onClick={() => setSelectedInterval('yearly')}
            >
              Yearly
              <span className="save-badge">Save {effectiveMonthly < monthlyAmount ? Math.round((1 - effectiveMonthly / monthlyAmount) * 100) : 0}%</span>
            </button>
          </div>

          <div className="upgrade-price-display">
            {selectedInterval === 'yearly' ? (
              <>
                <span className="price-amount">${money(effectiveMonthly)}</span>
                <span className="price-period">/month</span>
                <span className="price-billed">Billed annually (${money(yearlyAmount)}/year)</span>
              </>
            ) : (
              <>
                <span className="price-amount">${money(monthlyAmount)}</span>
                <span className="price-period">/month</span>
                <span className="price-billed">Billed monthly</span>
              </>
            )}
          </div>
        </div>

        <button
          className="upgrade-cta-btn"
          onClick={handleUpgrade}
          disabled={loading || !paddleReady}
        >
          {loading ? 'Loading...' : `Start Pro ${selectedInterval === 'yearly' ? 'Yearly' : 'Monthly'}`}
        </button>

        <p className="upgrade-guarantee">
          ✓ Cancel anytime &nbsp;•&nbsp; ✓ 7-day free trial
        </p>
      </div>

      <div className="upgrade-footer">
        <button className="upgrade-close-btn" onClick={close}>
          Maybe Later
        </button>
      </div>
    </div>
  );
};

export default UpgradeModal;
